import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, ChevronRight, Trophy } from 'lucide-react';
import { useGameLeaderboard, type ScoreEntry } from '../hooks/useGameLeaderboard';
import { useNostrProfile } from '../hooks/useNostrQuery';

const GAMES = [
  {
    id: 'chess',
    title: 'BCH Chess',
    description: 'Play chess with the BCH community over Nostr. Challenge friends, make your moves, and tip your opponent!',
    emoji: '♟\uFE0F',
    path: '/chess',
    color: 'from-amber-500/10 to-amber-600/5 border-amber-500/20',
    iconBg: 'bg-amber-500/10',
    badge: 'Multiplayer',
    badgeColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    hasLeaderboard: false,
  },
  {
    id: 'pacman',
    title: 'BCH Pac-Man',
    description: 'Collect BCH coins, dodge ghosts, and chase high scores in this classic arcade game. Share your best score on Nostr!',
    emoji: '\uD83D\uDFE1',
    path: '/games/pacman',
    color: 'from-yellow-500/10 to-yellow-600/5 border-yellow-500/20',
    iconBg: 'bg-yellow-500/10',
    badge: 'Single Player',
    badgeColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    hasLeaderboard: true,
  },
  {
    id: 'wordle',
    title: 'BCH Wordle',
    description: 'Guess the daily 5-letter word in 6 tries. New puzzle every day — share your results with the community!',
    emoji: '\uD83D\uDFE9',
    path: '/games/wordle',
    color: 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20',
    iconBg: 'bg-emerald-500/10',
    badge: 'Daily Puzzle',
    badgeColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    hasLeaderboard: true,
  },
  {
    id: 'poker',
    title: 'BCH Poker',
    description: 'Heads-up Texas Hold\'em over Nostr. Challenge a friend, bet chips, and see who has the best hand!',
    emoji: '🃏',
    path: '/games/poker',
    color: 'from-red-500/10 to-red-600/5 border-red-500/20',
    iconBg: 'bg-red-500/10',
    badge: 'Multiplayer',
    badgeColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    hasLeaderboard: false,
  },
];

export default function GamesPage() {
  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Crown className="w-5 h-5 text-primary" /> BCH Games
        </h1>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Hero */}
        <div className="text-center py-6">
          <div className="flex items-center justify-center gap-3 text-5xl mb-3">
            <span>{'♟\uFE0F'}</span>
            <span>{'🃏'}</span>
            <span>{'\uD83D\uDC7E'}</span>
            <span>{'\uD83C\uDFAE'}</span>
          </div>
          <h2 className="text-xl font-bold">BCH Games</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Take a break and play some games with the BCH community. Challenge friends, chase high scores, and have fun!
          </p>
        </div>

        {/* Game cards */}
        <div className="space-y-3">
          {GAMES.map(game => (
            <Link
              key={game.id}
              to={game.path}
              className={`block rounded-xl border bg-gradient-to-br ${game.color} p-4 hover:shadow-lg transition-all group`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl ${game.iconBg} flex items-center justify-center text-3xl shrink-0`}>
                  {game.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-base font-bold">{game.title}</h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${game.badgeColor}`}>
                      {game.badge}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{game.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
            </Link>
          ))}
        </div>

        {/* ── Top Scores ── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="text-base font-bold">Top Scores</h2>
          </div>
          <MiniLeaderboard gameId="pacman" title="Pac-Man" emoji={'\uD83D\uDFE1'} gamePath="/games/pacman" formatScore={(e) => `${e.score.toLocaleString()} pts`} />
          <MiniLeaderboard gameId="wordle" title="Wordle" emoji={'\uD83D\uDFE9'} gamePath="/games/wordle" formatScore={(e) => {
            const maxStreak = e.meta?.maxStreak ?? Math.floor(e.score / 100);
            const winRate = e.meta?.winRate ?? (e.score % 100);
            return `${maxStreak} streak · ${winRate}%`;
          }} />
        </div>

        {/* Coming soon teaser */}
        <div className="rounded-xl border border-dashed border-border/60 bg-muted/30 p-4 text-center space-y-2">
          <p className="text-sm font-medium text-muted-foreground">More games coming soon!</p>
          <p className="text-xs text-muted-foreground/60">
            Got a game idea? Drop it in the <Link to="/feedback" className="text-primary hover:underline">Suggestion Box</Link> and vote for the ones you want!
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Mini Leaderboard (top 3 for Games hub) ───────────────────────
function MiniLeaderboard({ gameId, title, emoji, gamePath, formatScore }: {
  gameId: string;
  title: string;
  emoji: string;
  gamePath: string;
  formatScore: (entry: ScoreEntry) => string;
}) {
  const { scores, loading } = useGameLeaderboard(gameId);
  const top3 = scores.slice(0, 3);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-3 py-2 border-b border-border/60 bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{emoji}</span>
          <span className="text-xs font-bold">{title}</span>
        </div>
        <Link to={gamePath} className="text-[10px] text-primary hover:underline font-medium">
          Play →
        </Link>
      </div>

      {loading && top3.length === 0 ? (
        <div className="px-4 py-3 text-center">
          <p className="text-xs text-muted-foreground">Loading...</p>
        </div>
      ) : top3.length === 0 ? (
        <div className="px-4 py-3 text-center">
          <p className="text-xs text-muted-foreground">No scores yet — <Link to={gamePath} className="text-primary hover:underline">be the first!</Link></p>
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {top3.map((entry, i) => (
            <MiniRow key={entry.pubkey} entry={entry} rank={i + 1} formatScore={formatScore} />
          ))}
        </div>
      )}

      {scores.length > 3 && (
        <div className="px-3 py-2 border-t border-border/60 text-center">
          <Link to={gamePath} className="text-[10px] text-primary hover:underline font-medium">
            View all {scores.length} players →
          </Link>
        </div>
      )}
    </div>
  );
}

function MiniRow({ entry, rank, formatScore }: { entry: ScoreEntry; rank: number; formatScore: (e: ScoreEntry) => string }) {
  const { profile } = useNostrProfile(entry.pubkey);
  const name = profile?.name || profile?.display_name || entry.pubkey.slice(0, 8) + '...';
  const avatar = profile?.picture;
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;

  return (
    <Link
      to={`/profile/${entry.pubkey}`}
      className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 transition-colors"
    >
      <span className="text-sm w-6 text-center shrink-0">{medal || rank}</span>
      <div className="w-6 h-6 rounded-full bg-muted overflow-hidden shrink-0">
        {avatar ? (
          <img src={avatar} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[10px] font-bold">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <span className="text-xs font-medium truncate flex-1">{name}</span>
      <span className={`text-xs font-bold tabular-nums shrink-0 ${rank === 1 ? 'text-amber-500' : ''}`}>
        {formatScore(entry)}
      </span>
    </Link>
  );
}

